import React from 'react';
import { CountdownTimer } from '../components/CountdownTimer';
import { PurchaseNotification } from '../components/PurchaseNotification';
import { FAQ } from '../components/FAQ';
import { Check, Star, ArrowRight, ShieldCheck } from 'lucide-react';
export function LandingPage() {
  return <div className="min-h-screen bg-black font-sans text-stone-100 selection:bg-amber-900/50" data-id="element-23">
      <CountdownTimer data-id="element-24" />
      <PurchaseNotification data-id="element-25" />

      {/* Spacer for fixed timer */}
      <div className="h-[48px]" data-id="element-26"></div>

      {/* Hero Section */}
      <header className="relative pt-8 pb-8 px-4 md:px-8 text-center overflow-hidden" data-id="element-27">
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-28">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-400 to-amber-200 mb-6 leading-[1.1]" data-id="element-29">
            Stop Fighting Your Body.
          </h1>
          <p className="text-xl md:text-2xl text-amber-100/80 max-w-2xl mx-auto leading-relaxed" data-id="element-30">
            Some weeks, you're unstoppable.
          </p>
        </div>
        {/* Decorative background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-to-br from-amber-500/20 to-yellow-600/10 rounded-full blur-3xl -z-10" data-id="element-31"></div>
      </header>

      {/* Problem Section */}
      <section className="py-6 px-4 md:px-8 bg-gradient-to-b from-black to-stone-900" data-id="element-32">
        <div className="max-w-3xl mx-auto space-y-6 text-lg md:text-xl leading-relaxed text-stone-300" data-id="element-33">
          <p className="font-medium text-amber-100" data-id="element-34">
            Focused. Creative. Clear. You check things off your list with ease
            and think, "This is who I really am."
          </p>

          <div className="pl-6 border-l-4 border-amber-500/50 italic text-amber-200/70" data-id="element-35">
            And then… next week...
          </div>

          {/* Image */}
          <div className="my-6 rounded-2xl overflow-hidden border border-amber-500/20" data-id="element-36">
            <img src="https://cdn.magicpatterns.com/uploads/8hi37sGBiGxDvEPspTFab2/1.png" alt="Contrast between professional confidence and exhaustion" className="w-full h-auto object-contain" data-id="element-37" />
          </div>

          <p data-id="element-38">
            Brain fog. Irritable. Exhausted. Simple tasks feel heavy. Your
            motivation disappears — and you start wondering:
          </p>

          <p className="font-bold text-2xl text-amber-300" data-id="element-39">
            "What happened to me?"
          </p>

          <p data-id="element-40">
            So you push harder. Drink more caffeine. Promise to "be more
            disciplined."
          </p>

          <p data-id="element-41">And the cycle repeats.</p>
        </div>
      </section>

      {/* Truth Reveal */}
      <section className="py-8 px-4 md:px-8 bg-stone-950" data-id="element-42">
        <div className="max-w-3xl mx-auto text-center space-y-6" data-id="element-43">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-300" data-id="element-44">
            Here's the truth no one told you:
          </h2>

          <div className="text-xl md:text-2xl leading-relaxed space-y-4" data-id="element-45">
            <p data-id="element-46">
              Your body isn't broken.{' '}
              <span className="text-amber-400 font-bold" data-id="element-47">
                Your productivity system is.
              </span>
            </p>

            <p className="text-stone-400" data-id="element-48">
              Traditional planners assume constant energy. Your hormones don't
              work that way.
            </p>

            <p className="text-stone-400" data-id="element-49">
              Every 7 days, your energy, focus, creativity, and stress tolerance
              shift — dramatically. Yet you're expected to perform the same
              every single day.
            </p>

            <p className="font-medium text-amber-100" data-id="element-50">
              No wonder you're tired. No wonder consistency feels impossible. No
              wonder rest feels like failure.
            </p>
          </div>
        </div>
      </section>

      {/* Cyclical Nature */}
      <section className="py-8 px-4 md:px-8 bg-gradient-to-b from-stone-950 to-black" data-id="element-51">
        <div className="max-w-4xl mx-auto" data-id="element-52">
          <div className="text-center" data-id="element-53">
            <h2 className="text-4xl md:text-5xl font-bold text-stone-100 mb-4" data-id="element-54">
              You're Not Inconsistent.
              <br data-id="element-55" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-500" data-id="element-56">
                You're Cyclical.
              </span>
            </h2>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-8 px-4 md:px-8 bg-black border-t border-amber-500/10" data-id="element-57">
        <div className="max-w-4xl mx-auto text-center" data-id="element-58">
          <span className="inline-block px-4 py-1 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-300 border border-amber-500/30 rounded-full text-sm font-bold tracking-wide mb-4" data-id="element-59">
            NEW SYSTEM
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-amber-100 mb-6" data-id="element-60">
            Introducing the Cycle-Smart Planner (FLOW-4™)
          </h2>
          <p className="text-xl text-stone-300 mb-8 max-w-2xl mx-auto" data-id="element-61">
            A 28-day, science-aligned planning system that teaches you how to
            work with your hormonal rhythm — instead of against it.
          </p>

          <div className="bg-stone-900 p-8 md:p-10 rounded-3xl border border-amber-500/20 text-left shadow-2xl" data-id="element-62">
            <h3 className="text-2xl font-bold mb-6 text-center text-amber-200" data-id="element-63">
              Inside, you'll learn how to:
            </h3>
            <ul className="space-y-4 max-w-2xl mx-auto" data-id="element-64">
              {['Schedule deep work when focus naturally peaks', 'Plan rest before burnout hits', 'Reduce PMS symptoms naturally', 'Finish more tasks with less effort', 'Rebuild trust in yourself'].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-65">
                  <div className="bg-gradient-to-br from-amber-500/20 to-yellow-600/20 p-1 rounded-full mt-1 flex-shrink-0 border border-amber-500/30" data-id="element-66">
                    <Check className="w-5 h-5 text-amber-400" data-id="element-67" />
                  </div>
                  <span className="text-lg md:text-xl text-stone-200" data-id="element-68">
                    {item}
                  </span>
                </li>)}
            </ul>
            <p className="text-center mt-8 text-xl font-medium text-amber-400 italic" data-id="element-69">
              This isn't motivation. It's alignment.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-8 px-4 md:px-8 bg-stone-950" data-id="element-70">
        <div className="max-w-6xl mx-auto" data-id="element-71">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-amber-200" data-id="element-72">
            What Women Are Saying
          </h2>

          <div className="grid md:grid-cols-3 gap-6 mb-8" data-id="element-73">
            {[{
            quote: "For the first time, I don't feel broken.",
            author: 'Andrea M., Entrepreneur'
          }, {
            quote: "My PMS doesn't control my life anymore.",
            author: 'Sofia R., Marketing Manager'
          }, {
            quote: 'This feels like a system I can actually keep using.',
            author: 'Camila L., Busy Mom & Freelancer'
          }].map((t, i) => <div key={i} className="bg-black p-6 rounded-2xl border border-amber-500/20" data-id="element-74">
                <div className="flex gap-1 mb-3" data-id="element-75">
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 text-amber-400 fill-amber-400" data-id="element-76" />)}
                </div>
                <p className="text-lg mb-4 italic text-stone-300" data-id="element-77">
                  "{t.quote}"
                </p>
                <p className="font-bold text-amber-400" data-id="element-78">— {t.author}</p>
              </div>)}
          </div>

          <div className="bg-gradient-to-br from-stone-900 to-black rounded-3xl p-8 md:p-10 text-center border border-amber-500/20" data-id="element-79">
            <h3 className="text-2xl font-bold mb-6 text-amber-200" data-id="element-80">
              Women using FLOW-4™ report:
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-81">
              {[{
              val: '70%',
              label: 'reduction in PMS symptoms'
            }, {
              val: '85%+',
              label: 'task completion'
            }, {
              val: 'Fewer',
              label: 'burnout crashes'
            }, {
              val: 'More',
              label: 'confidence and self-trust'
            }].map((stat, i) => <div key={i} className="space-y-2" data-id="element-82">
                  <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-400" data-id="element-83">
                    {stat.val}
                  </div>
                  <div className="text-sm md:text-base text-stone-400" data-id="element-84">
                    {stat.label}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Checklist */}
      <section className="py-8 px-4 md:px-8 bg-black" data-id="element-85">
        <div className="max-w-3xl mx-auto" data-id="element-86">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-amber-200" data-id="element-87">
            This Is For You If…
          </h2>
          <div className="space-y-3" data-id="element-88">
            {['You feel productive one week and exhausted the next', "You've tried planners that didn't stick", "You're tired of blaming yourself for energy dips", 'You want structure without forcing yourself', "You're ready to work smarter — not harder", 'You want to feel like yourself again'].map((item, i) => <div key={i} className="flex items-center gap-4 bg-stone-900 p-4 rounded-xl border border-amber-500/20" data-id="element-89">
                <div className="bg-gradient-to-br from-amber-500/20 to-yellow-600/20 p-1.5 rounded-full flex-shrink-0 border border-amber-500/30" data-id="element-90">
                  <Check className="w-5 h-5 text-amber-400" data-id="element-91" />
                </div>
                <span className="text-lg font-medium text-stone-200" data-id="element-92">
                  {item}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-8 px-4 md:px-8 bg-stone-950 overflow-hidden" data-id="element-93">
        <div className="max-w-5xl mx-auto" data-id="element-94">
          {/* Pricing Card */}
          <div className="max-w-2xl mx-auto bg-gradient-to-br from-stone-900 to-black rounded-[2.5rem] shadow-2xl border-4 border-amber-500/30 overflow-hidden relative mb-8" data-id="element-95">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-400" data-id="element-96"></div>
            <div className="p-8 md:p-10 text-center" data-id="element-97">
              {/* Product Image */}
              <div className="mb-6 rounded-2xl overflow-hidden border border-amber-500/20" data-id="element-98">
                <img src="https://cdn.magicpatterns.com/uploads/w2RFL4Br4PLGZ5k1yk9QNy/ChatGPT_Image_19_dic_2025,_09_18_57_p.m..png" alt="FLOW-4 Cycle-Smart Planner on devices" className="w-full h-auto object-contain" data-id="element-99" />
              </div>

              <p className="text-stone-400 text-lg mb-2" data-id="element-100">
                Total Value: <span className="line-through" data-id="element-101">$199+</span>
              </p>
              <div className="flex items-center justify-center gap-4 mb-2" data-id="element-102">
                <span className="text-3xl text-stone-500 line-through font-medium" data-id="element-103">
                  $49.97
                </span>
                <span className="text-6xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-300" data-id="element-104">
                  $9.97
                </span>
              </div>
              <p className="text-amber-400 font-medium mb-6" data-id="element-105">
                That's less than a Starbucks coffee.
              </p>

              <a href="https://www.mialmazen.com/cart/47518474698890:1" className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black text-xl md:text-2xl font-bold py-6 rounded-xl shadow-lg hover:shadow-amber-500/50 transition-all transform hover:-translate-y-1 mb-6 flex items-center justify-center gap-3" data-id="element-106">
                Get FLOW-4™ Now <ArrowRight className="w-6 h-6" data-id="element-107" />
              </a>

              <div className="text-sm text-stone-400 space-y-1" data-id="element-108">
                <p data-id="element-109">Let's put this in perspective:</p>
                <p data-id="element-110">Other programs charge $199+ for less guidance</p>
                <p data-id="element-111">Hormone health coaches charge $150+ per session.</p>
                <p data-id="element-112">Cycle-syncing courses can run into the thousands</p>
                <p className="font-medium text-stone-300 mt-3" data-id="element-113">
                  FLOW-4™ gives you a complete, proven system to feel aligned,
                  productive, and grounded — for the price of lunch.
                </p>
              </div>
            </div>
          </div>

          {/* Bonuses Section */}
          <div className="max-w-3xl mx-auto" data-id="element-114">
            {/* Bonuses Image */}
            <div className="mb-6 rounded-2xl overflow-hidden border border-amber-500/20" data-id="element-115">
              <img src="https://cdn.magicpatterns.com/uploads/sVp4SnMw2aTUFQY438qwyX/ChatGPT_Image_29_dic_2025,_07_47_09_p.m..png" alt="FLOW-4 Bonus Materials" className="w-full h-auto object-contain" data-id="element-116" />
            </div>

            <div className="bg-black p-8 rounded-3xl border border-amber-500/20" data-id="element-117">
              <h3 className="text-2xl font-bold text-amber-300 mb-6 text-center" data-id="element-118">
                Plus Bonuses:
              </h3>
              <ul className="space-y-4" data-id="element-119">
                {[{
                title: 'BONUS 1: Nutrition Guide & Recommendations',
                desc: 'What to eat during each phase to support energy, mood, and focus'
              }, {
                title: 'BONUS 2: Printable Phase Calendar',
                desc: 'Visual clarity for planning your month with confidence'
              }, {
                title: 'BONUS 3: Connect With Your Body — Listen to Your Mind',
                desc: 'Guided reflections to deepen self-trust and awareness'
              }, {
                title: 'BONUS 4: Cycle Tracker Calendar',
                desc: 'Track patterns, symptoms, and wins in one simple place'
              }].map((bonus, i) => <li key={i} className="flex gap-4" data-id="element-120">
                    <span className="text-2xl" data-id="element-121">🎁</span>
                    <div data-id="element-122">
                      <p className="font-bold text-amber-200" data-id="element-123">{bonus.title}</p>
                      <p className="text-sm text-stone-400" data-id="element-124">{bonus.desc}</p>
                    </div>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-8 px-4 md:px-8 bg-black" data-id="element-125">
        <div className="max-w-3xl mx-auto text-center" data-id="element-126">
          <ShieldCheck className="w-16 h-16 text-amber-400 mx-auto mb-4" data-id="element-127" />
          <h2 className="text-3xl md:text-4xl font-bold text-amber-200 mb-4" data-id="element-128">
            Try It Risk-Free
          </h2>
          <p className="text-xl text-stone-300 mb-6 leading-relaxed" data-id="element-129">
            Use FLOW-4™ for 7 days. If it doesn't feel supportive, aligned, and
            grounding — you'll get your money back.
          </p>
          <p className="text-2xl font-bold text-amber-100" data-id="element-130">
            No guilt. No questions. Just integrity.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-8 px-4 md:px-8 bg-stone-950" data-id="element-131">
        <div className="max-w-3xl mx-auto" data-id="element-132">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-amber-200" data-id="element-133">
            Frequently Asked Questions
          </h2>
          <FAQ data-id="element-134" />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-10 px-4 md:px-8 bg-black text-center border-t border-amber-500/10" data-id="element-135">
        <div className="max-w-3xl mx-auto" data-id="element-136">
          <h2 className="text-3xl md:text-5xl font-bold text-stone-100 mb-6" data-id="element-137">
            You don't need more willpower.
            <br data-id="element-138" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-500" data-id="element-139">
              You need a system designed for you.
            </span>
          </h2>
          <a href="https://www.mialmazen.com/cart/47518474698890:1" className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black text-xl md:text-2xl font-bold py-6 px-12 rounded-full shadow-lg hover:shadow-amber-500/50 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-3 mx-auto inline-flex" data-id="element-140">
            👉 Get the Cycle-Smart Planner now — and step into flow.
          </a>
        </div>
      </section>
    </div>;
}